import { fetcher } from '@common/utils/fetcher';

export type PaymentProvider = 'telegram_stars' | 'rustore_pay' | 'yookassa';

export type CheckoutStatus =
  | 'created'
  | 'awaiting_user_action'
  | 'processing'
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'expired';

export type CheckoutPresentation =
  | {
      kind: 'telegram_invoice_link';
      url: string;
    }
  | {
      kind: 'rustore_pay_subscription';
      productId: string;
      orderId: string;
      appUserId: string;
      appUserEmail?: string | null;
      developerPayload: string;
      returnUrl?: string;
    }
  | {
      kind: 'external_url';
      url: string;
      returnUrl?: string;
    };

export type BillingSubscriptionStatus =
  | 'pending'
  | 'active'
  | 'cancelled'
  | 'expired'
  | 'stopped';

export interface BillingPrice {
  id: string;
  provider: PaymentProvider;
  currency: string;
  amount: number;
  isActive: boolean;
  legacyPlanId: number | null;
  providerProductRef?: string | null;
}

export interface BillingProduct {
  id: string;
  productCode: string;
  name: string;
  description?: string;
  durationDays: number;
  isActive: boolean;
  prices: BillingPrice[];
}

export interface CheckoutSession {
  sessionId: string;
  provider: PaymentProvider;
  status: CheckoutStatus;
  presentation?: CheckoutPresentation;
  productCode: string;
  productName: string;
  durationDays: number;
  amount: number;
  currency: string;
  premiumUntil?: string | null;
  failureCode?: string | null;
  lastUpdatedAt: string;
}

export interface SubscriptionPaymentMethodCard {
  first6: string | null;
  last4: string | null;
  cardType: string | null;
  expiryMonth: string | null;
  expiryYear: string | null;
}

export interface SubscriptionPaymentMethod {
  type: string | null;
  title: string | null;
  card: SubscriptionPaymentMethodCard | null;
}

export interface CurrentBillingSubscription {
  subscriptionId: string;
  provider: Extract<PaymentProvider, 'rustore_pay' | 'yookassa'>;
  productCode: string | null;
  planName: string | null;
  status: BillingSubscriptionStatus;
  autoRenewing: boolean;
  startedAt?: string | null;
  expiresAt?: string | null;
  nextChargeAt?: string | null;
  amount?: number | null;
  currency?: string | null;
  paymentMethod?: SubscriptionPaymentMethod | null;
  cancelReason?: string | null;
  premiumUntil?: string | null;
}

export type PaymentHistoryStatus =
  | 'processing'
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'refunded';

export interface PaymentHistoryItem {
  id: string;
  provider: PaymentProvider;
  productCode: string | null;
  amount: number;
  currency: string;
  status: PaymentHistoryStatus;
  paidAt: string | null;
  refundedAt: string | null;
}

export interface PaymentHistoryPagination {
  limit: number;
  hasMore: boolean;
  nextCursor: string | null;
}

export type GetPaymentsCatalogResponse = {
  products: BillingProduct[];
  /**
   * True when the mini-app paywall may offer the bank-card (YooKassa)
   * option — gated server-side by YOOKASSA_ALLOW_TMA_CHECKOUT. Absent or
   * false keeps the paywall Stars-only (prod-safe default).
   */
  cardPaymentsAvailable?: boolean;
  /**
   * True when the paywall may offer the auto-renewing SberPay (YooKassa)
   * option — gated server-side by YOOKASSA_SBERPAY_RECURRING. Absent or
   * false hides SberPay.
   */
  sberpayRecurringAvailable?: boolean;
  /**
   * True when the paywall may offer the auto-renewing СБП (YooKassa) option
   * — gated server-side by YOOKASSA_SBP_RECURRING (the shop's recurring
   * capability covers SberPay and СБП). Absent or false hides СБП.
   */
  sbpRecurringAvailable?: boolean;
};

/**
 * YooKassa payment-page scoping for a checkout session: 'sberbank' = SberPay
 * and 'sbp' = СБП (the auto-renewing methods the shop can save),
 * 'bank_card' = one-off card. Omit to keep the provider's default payment
 * page.
 */
export type CheckoutPaymentMethodType = 'sberbank' | 'sbp' | 'bank_card';

export type PostPaymentsCheckoutSessionsParams = {
  productCode: string;
  appInstanceId: string;
  surface?: string;
  provider?: PaymentProvider;
  paymentMethodType?: CheckoutPaymentMethodType;
};

export type PostPaymentsCheckoutSessionsResponse = CheckoutSession;

export type GetPaymentsCheckoutSessionsByIdParams = {
  sessionId: string;
};

export type GetPaymentsCheckoutSessionsByIdResponse = CheckoutSession;

export type GetPaymentsSubscriptionsCurrentResponse =
  CurrentBillingSubscription | null;

export type PostPaymentsSubscriptionsCancelAutoRenewParams = {
  subscriptionId: string;
};

export type PostPaymentsSubscriptionsCancelAutoRenewResponse =
  CurrentBillingSubscription;

export type GetPaymentsHistoryParams = {
  limit?: number;
  before?: string;
};

export type GetPaymentsHistoryResponse = {
  payments: PaymentHistoryItem[];
  pagination: PaymentHistoryPagination;
};

export const getPaymentsCatalog = () => {
  return fetcher<GetPaymentsCatalogResponse>({
    url: '/payments/catalog',
    method: 'GET',
  });
};

export const postPaymentsCheckoutSessions = (
  data: PostPaymentsCheckoutSessionsParams,
) => {
  return fetcher<PostPaymentsCheckoutSessionsResponse>({
    url: '/payments/checkout-sessions',
    method: 'POST',
    data,
  });
};

export const getPaymentsCheckoutSessionsById = ({
  sessionId,
}: GetPaymentsCheckoutSessionsByIdParams) => {
  return fetcher<GetPaymentsCheckoutSessionsByIdResponse>({
    url: `/payments/checkout-sessions/${sessionId}`,
    method: 'GET',
  });
};

export const getPaymentsSubscriptionsCurrent = () => {
  return fetcher<GetPaymentsSubscriptionsCurrentResponse>({
    url: '/payments/subscriptions/current',
    method: 'GET',
  });
};

export const postPaymentsSubscriptionsCancelAutoRenew = ({
  subscriptionId,
}: PostPaymentsSubscriptionsCancelAutoRenewParams) => {
  return fetcher<PostPaymentsSubscriptionsCancelAutoRenewResponse>({
    url: `/payments/subscriptions/${subscriptionId}/cancel-auto-renew`,
    method: 'POST',
  });
};

export const getPaymentsHistory = (params: GetPaymentsHistoryParams = {}) => {
  return fetcher<GetPaymentsHistoryResponse>({
    url: '/payments/history',
    method: 'GET',
    params,
  });
};
